import { useContext, useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ChevronsLeftRight,
  CircleHelp,
  Coins,
  FileBox,
  Gauge,
  Home,
  KeyRound,
  LayoutDashboard,
  Receipt,
  Repeat2,
  Sparkles,
  Tags,
  Users,
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import { TitleContext } from "../../contexts/TitleContext";
import { SideMenuContext } from "../../contexts/SideMenuContext";
import { TOASTPROPS } from "../../shared/Constants";
import HelpForm from "../HelpForm";
import logo from "../../assets/maritaca_logo.png";
import logoBranca from "../../assets/maritaca_logo_branca.png";
import { ThemeContext } from "../../contexts/ThemeContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { cn } from "../../lib/utils";
import UserMenu from "./UserMenu";

type Item = { to: string; title: string; icon: React.ComponentType<{ className?: string }> };
type Section = { label: string; items: Item[] };

const sections: Section[] = [
  {
    label: "Dashboard",
    items: [
      { to: "/home", title: "Início", icon: Home },
      { to: "/gastos", title: "Gastos", icon: LayoutDashboard },
    ],
  },
  {
    label: "Desenvolvimento",
    items: [
      { to: "/modelos", title: "Modelos", icon: Sparkles },
      { to: "/precos", title: "Preços", icon: Tags },
      { to: "/rate-limits", title: "Rate Limits", icon: Gauge },
      { to: "/chaves-de-api", title: "Chaves de API", icon: KeyRound },
      { to: "/organizacoes", title: "Organizações", icon: Users },
    ],
  },
  {
    label: "Ferramentas",
    items: [
      { to: "/batch", title: "Batch API", icon: Repeat2 },
      { to: "/arquivos", title: "Arquivos", icon: FileBox },
      { to: "/playground", title: "Playground", icon: ChevronsLeftRight },
    ],
  },
  {
    label: "Conta",
    items: [
      { to: "/recarga", title: "Recarga", icon: Coins },
      { to: "/comprovantes", title: "Comprovantes", icon: Receipt },
    ],
  },
];

const titleByPath: Record<string, string> = {
  "/chaves-de-api": "Chaves de API",
  "/organizacoes": "Organizações",
  "/recarga": "Recarga",
  "/comprovantes": "Comprovantes",
  "/modelos": "Modelos",
  "/precos": "Preços",
  "/rate-limits": "Rate Limits",
  "/gastos": "Gastos",
  "/": "Plataforma",
  "/home": "Plataforma",
  "/batch": "Batch API",
  "/arquivos": "Arquivos",
  "/playground": "Playground",
};

function NavItem({
  item,
  active,
  collapsed,
  onClick,
}: {
  item: Item;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
}) {
  const Icon = item.icon;
  const link = (
    <NavLink
      to={item.to}
      onClick={onClick}
      className={cn(
        "group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[14px] font-medium transition-colors",
        active
          ? "bg-background text-foreground shadow-[0_1px_2px_rgba(60,66,87,0.08),0_0_0_1px_rgba(60,66,87,0.06)]"
          : "text-muted-foreground hover:text-foreground hover:bg-background/60",
        collapsed && "justify-center px-2"
      )}
    >
      <Icon
        className={cn(
          "h-[17px] w-[17px] shrink-0",
          active ? "text-foreground" : "text-muted-foreground/80 group-hover:text-foreground"
        )}
      />
      {!collapsed && <span className="truncate">{item.title}</span>}
    </NavLink>
  );
  if (collapsed) {
    return (
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={10}>{item.title}</TooltipContent>
      </Tooltip>
    );
  }
  return link;
}

export default function SideMenu() {
  const { title, setTitle } = useContext(TitleContext);
  const { isDarkMode } = useContext(ThemeContext);
  const { openHelp, setOpenHelp, toastMessage, isSideMenuCollapsed, setIsSideMenuCollapsed } =
    useContext(SideMenuContext);
  const collapsed = isSideMenuCollapsed;

  useEffect(() => {
    if (!toastMessage) return;
    const fn = (toast as any)[toastMessage.type] ?? toast.info;
    fn(toastMessage.message, TOASTPROPS);
  }, [toastMessage]);

  useEffect(() => {
    const sync = () => setTitle(titleByPath[window.location.pathname] ?? "Plataforma");
    sync();
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, [setTitle]);

  return (
    <TooltipProvider>
      <aside
        data-collapsed={collapsed}
        className={cn(
          "relative flex h-screen flex-col overflow-hidden border-r border-border bg-surface transition-[width] duration-200",
          collapsed ? "w-[64px]" : "w-[244px]",
          "max-md:absolute max-md:z-40 max-md:h-screen",
          collapsed && "max-md:-translate-x-full"
        )}
      >
        <div className={cn("flex items-center gap-2 px-4 pt-4 pb-3", collapsed && "justify-center px-2")}>
          <a
            href="https://maritaca.ai/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2"
          >
            <img src={isDarkMode ? logoBranca : logo} alt="Maritaca" className="h-7 w-7" />
            {!collapsed && (
              <span className="text-[15.5px] font-semibold tracking-tight text-foreground">
                maritaca.ai
              </span>
            )}
          </a>
          {!collapsed && (
            <button
              onClick={() => setIsSideMenuCollapsed(true)}
              aria-label="Recolher menu"
              className="ml-auto flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {collapsed && (
          <button
            onClick={() => setIsSideMenuCollapsed(false)}
            aria-label="Expandir menu"
            className="mx-auto mb-2 flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        )}

        <nav className="flex flex-1 flex-col overflow-y-auto px-2.5 pb-2 [scrollbar-width:thin]">
          {sections.map((section) => (
            <div key={section.label} className="mb-3">
              {!collapsed && (
                <p className="mb-1 px-2.5 pt-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/70">
                  {section.label}
                </p>
              )}
              <div className="space-y-px">
                {section.items.map((item) => (
                  <NavItem
                    key={item.to}
                    item={item}
                    active={title === titleByPath[item.to]}
                    collapsed={collapsed}
                    onClick={() => setTitle(titleByPath[item.to])}
                  />
                ))}
              </div>
            </div>
          ))}

          <div className="mt-auto space-y-px border-t border-border pt-2">
            {collapsed ? (
              <>
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => setOpenHelp(true)}
                      className="flex w-full items-center justify-center rounded-md p-1.5 text-muted-foreground hover:bg-background/60 hover:text-foreground"
                    >
                      <CircleHelp className="h-[17px] w-[17px]" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={10}>Ajuda</TooltipContent>
                </Tooltip>
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => window.open("https://docs.maritaca.ai", "_blank")}
                      className="flex w-full items-center justify-center rounded-md p-1.5 text-muted-foreground hover:bg-background/60 hover:text-foreground"
                    >
                      <BookOpen className="h-[17px] w-[17px]" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={10}>Documentação</TooltipContent>
                </Tooltip>
              </>
            ) : (
              <>
                <button
                  onClick={() => setOpenHelp(true)}
                  className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13.5px] font-medium text-muted-foreground transition-colors hover:bg-background/60 hover:text-foreground"
                >
                  <CircleHelp className="h-[17px] w-[17px]" />
                  Ajuda
                </button>
                <button
                  onClick={() => window.open("https://docs.maritaca.ai", "_blank")}
                  className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13.5px] font-medium text-muted-foreground transition-colors hover:bg-background/60 hover:text-foreground"
                >
                  <BookOpen className="h-[17px] w-[17px]" />
                  Documentação
                </button>
              </>
            )}
          </div>
        </nav>

        <div className={cn("border-t border-border p-2", collapsed && "p-1.5")}>
          <UserMenu variant="sidebar" collapsed={collapsed} />
        </div>
      </aside>

      <Dialog open={openHelp} onOpenChange={(o) => !o && setOpenHelp(false)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ajuda</DialogTitle>
          </DialogHeader>
          <HelpForm />
        </DialogContent>
      </Dialog>

      <ToastContainer />
    </TooltipProvider>
  );
}
